import type { SyncFiltersPayload } from '@webWorkers'
import type { TaxGroupDetailsResponse } from '@services/dashboard/types'

import { getCursorFromUrl, getDefaultPageSize, userSawMonthlySalaryOrTax } from '@helpers'

import type { UpdateOrderPayload } from '@hooks/useTableControls'

import { TAX_V2_DEFAULT_SORTING } from '@constants'

import authActions from '../auth/actions'
import dashboardActions from '../dashboard/actions'
import filtersActions from '../filters/actions'
import actions from './actions'

const defaultPageSize = getDefaultPageSize()

export const initialState: TaxStore = {
  list: {
    cursor: null,
    data: [],
    error: null,
    loading: false,
    nextPageUrl: null,
    pageSize: defaultPageSize,
    prevPageUrl: null,
    ...TAX_V2_DEFAULT_SORTING,
  },
  kpiCharts: {
    data: undefined,
    error: null,
    loading: false,
  },
  editorNotification: {
    lastEditedItem: null,
    notifyUser: false,
  },
}

export type TaxReducerAction =
  | { type: typeof authActions.selectCompany.REQUEST }
  | {
      type: typeof filtersActions.initTaxListParamsFromUrl.REQUEST
      payload: SyncFiltersPayload['params']
    }
  | { type: typeof actions.fetchTaxListV2.REQUEST }
  | {
      type: typeof actions.fetchTaxListByPagingV2.REQUEST
      payload: { url: string }
    }
  | {
      type: typeof actions.fetchTaxListV2.SUCCESS
      payload: {
        data: TaxListData[]
        previous: BackendCallableLink
        next: BackendCallableLink
      }
    }
  | { type: typeof actions.fetchTaxListV2.FAILURE; payload: BackendError }
  | { type: typeof actions.updateOrderV2.REQUEST; payload: UpdateOrderPayload }
  | {
      type: typeof actions.updateRowsPerPageV2.REQUEST
      payload: { pageSize: number }
    }
  | { type: typeof filtersActions.toggleTaxListDateFilter.REQUEST }
  | { type: typeof filtersActions.updateTaxListFilters.REQUEST }
  | { type: typeof filtersActions.resetTaxListFilters.REQUEST }
  | { type: typeof dashboardActions.userViewTaxV2.SUCCESS; payload: number }
  | { type: typeof dashboardActions.createMonthlyTaxV2.REQUEST }
  | {
      type: typeof dashboardActions.createMonthlyTaxV2.SUCCESS
      payload: TaxGroupDetailsResponse
    }
  | { type: typeof dashboardActions.resetMonthlyTaxEditorNotification.REQUEST }
  | { type: typeof actions.triggerTaxListUpdate.REQUEST }
  | { type: typeof actions.resetPagination.REQUEST }

export default function reducer(state: TaxStore = initialState, action: TaxReducerAction): TaxStore {
  switch (action.type) {
    case authActions.selectCompany.REQUEST:
      return { ...initialState }

    case filtersActions.initTaxListParamsFromUrl.REQUEST: {
      return {
        ...state,
        list: {
          ...state.list,
          ...action.payload,
        },
      }
    }

    case actions.fetchTaxListV2.REQUEST:
      return {
        ...state,
        list: {
          ...state.list,
          loading: true,
        },
      }

    case actions.fetchTaxListByPagingV2.REQUEST: {
      const cursor = getCursorFromUrl(action.payload.url)
      return {
        ...state,
        list: {
          ...state.list,
          cursor,
          loading: true,
        },
      }
    }

    case actions.fetchTaxListV2.SUCCESS:
      return {
        ...state,
        list: {
          ...state.list,
          data: action.payload.data,
          prevPageUrl: action.payload.previous,
          nextPageUrl: action.payload.next,
          loading: false,
          error: null,
        },
        editorNotification: {
          ...state.editorNotification,
          notifyUser: true,
        },
      }

    case actions.fetchTaxListV2.FAILURE:
      return {
        ...state,
        list: {
          ...state.list,
          loading: false,
          error: action.payload,
        },
      }

    case actions.updateOrderV2.REQUEST:
      return {
        ...state,
        list: {
          ...state.list,
          cursor: null,
          loading: true,
          error: null,
          order: action.payload.order,
          orderBy: action.payload.orderBy,
        },
      }

    case actions.updateRowsPerPageV2.REQUEST:
      return {
        ...state,
        list: {
          ...state.list,
          cursor: null,
          loading: true,
          error: null,
          pageSize: action.payload.pageSize,
        },
      }

    case filtersActions.toggleTaxListDateFilter.REQUEST:
    case filtersActions.updateTaxListFilters.REQUEST:
    case filtersActions.resetTaxListFilters.REQUEST:
      return {
        ...state,
        list: {
          ...state.list,
          cursor: null,
          loading: true,
          error: null,
        },
        kpiCharts: {
          ...state.kpiCharts,
          error: null,
          loading: true,
        },
      }

    // user saw salary with "new" badge
    case dashboardActions.userViewTaxV2.SUCCESS:
      return {
        ...state,
        list: {
          ...state.list,
          data: userSawMonthlySalaryOrTax(state.list.data, action.payload),
        },
      }

    // notification
    case dashboardActions.createMonthlyTaxV2.REQUEST:
      return {
        ...state,
        editorNotification: {
          ...state.editorNotification,
          notifyUser: false,
        },
      }

    case dashboardActions.createMonthlyTaxV2.SUCCESS: {
      const { id, taxes } = action.payload
      return {
        ...state,
        editorNotification: {
          lastEditedItem: {
            id,
            subRows: taxes,
          },
          notifyUser: false,
        },
      }
    }

    case dashboardActions.resetMonthlyTaxEditorNotification.REQUEST:
      return {
        ...state,
        editorNotification: {
          ...initialState.editorNotification,
        },
      }

    case actions.triggerTaxListUpdate.REQUEST:
    case actions.resetPagination.REQUEST:
      return {
        ...state,
        list: {
          ...state.list,
          cursor: null,
        },
      }

    default:
      return state
  }
}
